import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./CertificateCardDashboard.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserCertificationRegistrationListAction,
  userRegistrationSaveEachCardAction,
  userRegistrationSaveEachCardResetAction,
} from "../../../actions/MyCertificates/certificatesAction";
import PersonIcon from "@mui/icons-material/Person";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CertificateSubmission from "../Workshops/CertificateSubmission";
import CertificationProblemDetails from "./CertificationProblemDetails";
import MySkeletonLoader from "../reusables/MySkeletonLoader";
import ReviewModal from "./ReviewModal";
import CongratulationsModal from "../Workshops/CongratulationsModal";
import { USER_CERTIFICATTION_REGISTRATION_SUBMIT_RESET } from "../../../constants/MyCertificates/CertificatesConstants";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const CertificateCardDashboard = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { registrations, loading } = useSelector(
    (state) => state.userCertificatesRegistrationListStore
  );
  const { success, submittedData } = useSelector(
    (state) => state.certificationSubmitGithubLinkStore
  );

  const [activeTab, setActiveTab] = useState("inprogress");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [problemDrawerOpen, setProblemDrawerOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [congratsOpen, setCongratsOpen] = useState(false);

  const allCategories = {
    All: "all",
    Frontend: "frontend-development",
    Backend: "backend-development",
    DevOps: "devops-development",
    Database: "database-development",
    Mobile: "mobile-development",
    Cloud: "cloud-development",
    "AI/ML": "aiml-development",
  };

  useEffect(() => {
    if (userInfo?.token) {
      dispatch(getUserCertificationRegistrationListAction());
      dispatch(userRegistrationSaveEachCardResetAction());
    }
  }, [userInfo, dispatch]);

  // Handle success state from submission
  useEffect(() => {
    if (success && submittedData) {
      // 1. Close the drawer
      setDrawerOpen(false);
      setProblemDrawerOpen(false);

      // 2. Update the tab to "underreview" (or based on submittedData.status)
      const newStatus = normalizeStatus(submittedData.status || "underreview");
      setActiveTab(newStatus);

      // 3. Refresh the registrations list to get updated data
      dispatch(getUserCertificationRegistrationListAction());

      // 4. Open congratulations modal
      setCongratsOpen(true);

      // Clean up drawer state
      setTimeout(() => {
        setDrawerContent(null);
        setSelectedCard(null);
      }, 300);
    }
  }, [success, submittedData, dispatch]);

  // Get category keys for display
  const categoryKeys = Object.keys(allCategories);

  // Visible categories based on screen size
  const desktopVisibleCategories = categoryKeys.slice(0, 4);
  const mobileVisibleCategories = categoryKeys.slice(0, 2);

  const normalizeStatus = (s) => {
    const normalized = String(s || "")
      .replace(/[_\s-]/g, "")
      .toLowerCase();
    if (normalized === "paymentpending") {
      return "completed";
    }
    return normalized;
  };

  // Transform registrations data to match card structure
  const transformedData = useMemo(() => {
    return (
      registrations?.map((reg) => {
        const problem = reg.problem || {};
        const paymentDetails = reg?.paymentDetails;
        const statusNormalized = normalizeStatus(
          reg.status || problem.status || "in_progress"
        );

        return {
          _id: reg._id,
          originalRegistration: reg,
          image: problem.image?.url || "https://via.placeholder.com/300x180",
          title: problem.title || "Untitled Problem",
          subtitle:
            problem.subtitle ||
            problem.title ||
            "Problem description not available",
          category: problem.slug || "other",
          description:
            problem.problemStatement?.description || problem.description || "",
          keyPointers:
            problem.problemStatement?.keyPointers || problem.keyPointers || [],
          expectedOutput: problem.expectedOutput || {},
          paymentDetails: paymentDetails || {},
          daysToComplete: problem.daysToComplete || {},
          resources: problem.resources || {},
          problemStatement: problem.problemStatement,
          registeredStudents: problem.totalRegistrations || 0,
          totalRegistrations: problem.totalRegistrations || 0,
          paymentStatus:
            reg.paymentDetails?.paymentStatus ||
            reg.paymentDetails?.paymentStatus ||
            "",
          progress: reg.progress || {},
          completionPercentage: reg.progress?.completionPercentage ?? 0,
          status: statusNormalized,
          createdAt: reg.createdAt || problem.createdAt,
          updatedAt: reg.updatedAt || problem.updatedAt,
          difficulty: problem.difficulty || "",
          prerequisites: problem.prerequisites || [],
          skills: problem.skills || [],
          isActive: reg.isActive ?? problem.isActive ?? true,
        };
      }) || []
    );
  }, [registrations]);

  // Filter data based on active tab and selected category
  const filteredData = useMemo(() => {
    const selectedCategoryValue = allCategories[selectedCategory];

    return transformedData.filter((item) => {
      const matchesTab =
        normalizeStatus(item.status) === normalizeStatus(activeTab);
      const matchesCategory =
        selectedCategoryValue === "all" ||
        item.category === selectedCategoryValue ||
        item.category?.toLowerCase() === selectedCategoryValue?.toLowerCase();

      return matchesTab && matchesCategory;
    });
  }, [transformedData, activeTab, selectedCategory]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsFilterDialogOpen(false);
  };

  const handleSubmitSolution = (card) => {
    setSelectedCard(card);
    setDrawerContent("submit");
    setDrawerOpen(true);
  };

  const handleViewDetails = (card) => {
    setSelectedCard(card);
    setDrawerContent("details");
    setProblemDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setDrawerContent(null);
      setSelectedCard(null);
    }, 300);
  };

  const history = useHistory();

  function CertificateEachCard({ card }) {
    return (
      <motion.div
        key={card._id}
        className="certificateCardDashboard__card"
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 1 }}
        whileHover={{ y: -8, transition: { duration: 0.2 } }}
        onClick={() => handleViewDetails(card)}
      >
        <div className="certificateCardDashboard__card-image-wrapper">
          <img
            src={card.image}
            alt={card.title}
            className="certificateCardDashboard__card-image"
          />
        </div>
        <div className="certificateCardDashboard__card-content">
          <div className="certificateCardDashboard__card-students">
            <PersonIcon />
            <span>{card?.totalRegistrations}+ registrations</span>
          </div>
          <h3 className="certificateCardDashboard__card-title">{card.title}</h3>
          <p className="certificateCardDashboard__card-subtitle">
            {card.subtitle}
          </p>
          <div className="certificateCardDashboard__card-actions">
            {card?.status === "underreview" ? (
              <motion.button
                className="certificateCardDashboard__btn certificateCardDashboard__btn--submit"
                onClick={(e) => {
                  e.stopPropagation();
                  setReviewOpen(true);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                In Review
              </motion.button>
            ) : card?.status === "completed" ||
              card?.status === "payment_pending" ? (
              <motion.button
                className={`certificateCardDashboard__btn ${
                  card?.status === "completed"
                    ? "certificateCardDashboard__btn--completed"
                    : "certificateCardDashboard__btn--submit"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  // handleSubmitSolution(card);
                  dispatch(userRegistrationSaveEachCardAction(card));
                  history.push(`/certificate/grab/${card?._id}`);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Grab Certificate
              </motion.button>
            ) : (
              <>
                <motion.button
                  className="certificateCardDashboard__btn certificateCardDashboard__btn--submit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubmitSolution(card);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Submit Solution
                </motion.button>
                <motion.button
                  className="certificateCardDashboard__btn certificateCardDashboard__btn--view"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(card);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Details
                </motion.button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  }
  console.log(filteredData);
  return (
    <div className="certificateCardDashboard">
      {/* Tabs */}
      <div className="certificateCardDashboard__tabs">
        <motion.button
          className={`certificateCardDashboard__tab ${
            activeTab === "inprogress"
              ? "certificateCardDashboard__tab--active"
              : ""
          }`}
          onClick={() => setActiveTab("inprogress")}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          In Progress
        </motion.button>
        <motion.button
          className={`certificateCardDashboard__tab ${
            activeTab === "underreview"
              ? "certificateCardDashboard__tab--active"
              : ""
          }`}
          onClick={() => setActiveTab("underreview")}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          In Review
        </motion.button>
        <motion.button
          className={`certificateCardDashboard__tab ${
            activeTab === "completed"
              ? "certificateCardDashboard__tab--active"
              : ""
          }`}
          onClick={() => setActiveTab("completed")}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="certificateCardDashboard__tab-label">
            Completed
            {transformedData?.find((e) => e?.status === "completed") && (
              <span className="certificateCardDashboard__pulse-dot" />
            )}
          </span>
        </motion.button>
      </div>

      {/* Filters */}
      <div className="certificateCardDashboard__filters">
        {/* Desktop Filters */}
        <div className="certificateCardDashboard__filters--desktop">
          {desktopVisibleCategories.map((category) => (
            <motion.button
              key={category}
              className={`certificateCardDashboard__filter-btn ${
                selectedCategory === category
                  ? "certificateCardDashboard__filter-btn--active"
                  : ""
              }`}
              onClick={() => setSelectedCategory(category)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
          <motion.button
            className="certificateCardDashboard__filter-icon-btn"
            onClick={() => setIsFilterDialogOpen(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 5.83333H7.5M7.5 5.83333C7.5 7.67428 8.99238 9.16667 10.8333 9.16667C12.6743 9.16667 14.1667 7.67428 14.1667 5.83333C14.1667 3.99238 12.6743 2.5 10.8333 2.5C8.99238 2.5 7.5 3.99238 7.5 5.83333ZM17.5 14.1667H12.5M12.5 14.1667C12.5 16.0076 11.0076 17.5 9.16667 17.5C7.32572 17.5 5.83333 16.0076 5.83333 14.1667C5.83333 12.3257 7.32572 10.8333 9.16667 10.8333C11.0076 10.8333 12.5 12.3257 12.5 14.1667Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </motion.button>
        </div>

        {/* Mobile Filters */}
        <div className="certificateCardDashboard__filters--mobile">
          {mobileVisibleCategories.map((category) => (
            <motion.button
              key={category}
              className={`certificateCardDashboard__filter-btn ${
                selectedCategory === category
                  ? "certificateCardDashboard__filter-btn--active"
                  : ""
              }`}
              onClick={() => setSelectedCategory(category)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
          <motion.button
            className="certificateCardDashboard__filter-icon-btn"
            onClick={() => setIsFilterDialogOpen(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 5.83333H7.5M7.5 5.83333C7.5 7.67428 8.99238 9.16667 10.8333 9.16667C12.6743 9.16667 14.1667 7.67428 14.1667 5.83333C14.1667 3.99238 12.6743 2.5 10.8333 2.5C8.99238 2.5 7.5 3.99238 7.5 5.83333ZM17.5 14.1667H12.5M12.5 14.1667C12.5 16.0076 11.0076 17.5 9.16667 17.5C7.32572 17.5 5.83333 16.0076 5.83333 14.1667C5.83333 12.3257 7.32572 10.8333 9.16667 10.8333C11.0076 10.8333 12.5 12.3257 12.5 14.1667Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </motion.button>
        </div>
      </div>

      {/* Filter Dialog */}
      <AnimatePresence>
        {isFilterDialogOpen && (
          <>
            <motion.div
              className="certificateCardDashboard__overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsFilterDialogOpen(false)}
            />
            <motion.div
              className="certificateCardDashboard__dialog"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="certificateCardDashboard__dialog-header">
                <h3>Select Category</h3>
                <button
                  className="certificateCardDashboard__dialog-close"
                  onClick={() => setIsFilterDialogOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="certificateCardDashboard__dialog-content">
                {categoryKeys.map((category) => (
                  <motion.button
                    key={category}
                    className={`certificateCardDashboard__dialog-option ${
                      selectedCategory === category
                        ? "certificateCardDashboard__dialog-option--active"
                        : ""
                    }`}
                    onClick={() => handleCategorySelect(category)}
                    whileHover={{ scale: 1.02, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {category}
                    {selectedCategory === category && (
                      <span className="certificateCardDashboard__dialog-checkmark">
                        ✓
                      </span>
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Cards Grid */}
      <div className="certificateCardDashboard__grid">
        {loading ? (
          <div className="certificateDashboard__loader">
            <MySkeletonLoader height={"500px"} />
            <MySkeletonLoader height={"500px"} />
            <MySkeletonLoader height={"500px"} />
          </div>
        ) : (
          filteredData?.map((card) => (
            <CertificateEachCard card={card} key={card._id} />
          ))
        )}
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && !loading && (
        <motion.div
          className="certificateCardDashboard__empty"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p>No certificates found</p>
        </motion.div>
      )}

      {/* MUI Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        className="certificateCardDashboard__mui-drawer"
        PaperProps={{
          className: "certificateCardDashboard__drawer-paper",
        }}
      >
        <div className="certificateCardDashboard__drawer-header">
          <IconButton
            onClick={handleDrawerClose}
            className="certificateCardDashboard__drawer-close-btn"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="certificateCardDashboard__drawer-body">
          {drawerContent === "submit" && (
            <CertificateSubmission enrollment={selectedCard} />
          )}
        </div>
      </Drawer>

      {drawerContent === "details" && (
        <CertificationProblemDetails
          open={problemDrawerOpen}
          onClose={() => setProblemDrawerOpen(false)}
          problemData={selectedCard}
          buttonText={
            selectedCard?.status === "underreview"
              ? "In Review"
              : selectedCard?.status === "completed"
              ? "Grab Certificate"
              : "Submit Solution"
          }
          onButtonClick={() => {
            if (selectedCard?.status === "underreview") {
              setReviewOpen(true);
            } else {
              handleSubmitSolution(selectedCard);
            }
          }}
          pricingData={selectedCard?.paymentDetails}
          openedFrom={"dashboard"}
        />
      )}

      <ReviewModal open={reviewOpen} handleClose={() => setReviewOpen(false)} />

      {/* Congratulations Modal */}
      <CongratulationsModal
        open={congratsOpen}
        handleClose={() => {
          setCongratsOpen(false);
          dispatch({ type: USER_CERTIFICATTION_REGISTRATION_SUBMIT_RESET });
        }}
        submittedData={submittedData}
        username={userInfo?.firstName}
      />
    </div>
  );
};

export default CertificateCardDashboard;
